import { Facebook, Linkedin, Instagram, Twitter, Youtube, Music2, Plus, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export const SOCIAL_PLATFORMS = [
  { value: 'facebook', label: 'Facebook', icon: Facebook },
  { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'x', label: 'X (Twitter)', icon: Twitter },
  { value: 'youtube', label: 'YouTube', icon: Youtube },
  { value: 'tiktok', label: 'TikTok', icon: Music2 },
];

const iconFor = (platform) => (SOCIAL_PLATFORMS.find((p) => p.value === platform) || {}).icon || Plus;

export function SocialLinks({ links = [] }) {
  const valid = (links || []).filter((s) => s.url);
  if (valid.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="social-links">
      {valid.map((s, i) => {
        const Icon = iconFor(s.platform);
        return (
          <a
            key={`${s.platform}-${i}`}
            href={s.url.startsWith('http') ? s.url : `https://${s.url}`}
            target="_blank"
            rel="noreferrer"
            title={s.platform}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-magenta/10 hover:text-magenta text-slate-600 flex items-center justify-center transition"
            data-testid={`social-link-${s.platform}-${i}`}
          >
            <Icon className="w-4 h-4" />
          </a>
        );
      })}
    </div>
  );
}

export function SocialMediaEditor({ value = [], onChange }) {
  const rows = value || [];
  const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = () => onChange([...rows, { platform: 'facebook', url: '' }]);
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));

  return (
    <div data-testid="social-media-editor">
      <div className="text-[11px] uppercase tracking-wider font-bold text-magenta mb-2">Social Media</div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Select value={r.platform} onValueChange={(v) => update(i, { platform: v })}>
              <SelectTrigger className="w-40 shrink-0" data-testid={`social-platform-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOCIAL_PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              placeholder="Profile URL"
              value={r.url}
              onChange={(e) => update(i, { url: e.target.value })}
              data-testid={`social-url-${i}`}
            />
            <button type="button" onClick={() => remove(i)} className="text-slate-400 hover:text-rose-500 shrink-0" data-testid={`social-remove-${i}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="social-add">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add social profile
        </Button>
      </div>
    </div>
  );
}
